import { z } from "zod";
import { documentLanguageSchema } from "../documents/common.js";
import { invoiceTypeSchema } from "../documents/invoice/model.js";
import { incomeStatementCurrencyDisplaySchema } from "../documents/incomeStatement/model.js";
/**
 * Request schemas for the PDF/document API (`apps/api/src/routes/platform/pdf.ts`).
 *
 * These describe what a CALLER sends — identifiers and presentation params
 * only. Everything else (the org's currency, jurisdiction, logo, the report
 * title) is resolved server-side by the assemblers in `../documents/`, so a
 * document can be produced with nothing but a bearer token: no browser
 * session, no client-side shaping. That is the property phase 6 (scheduled /
 * emailed documents) is built on.
 *
 * Two endpoints share each schema — `/model` returns the assembled
 * DocumentModel as JSON, and the PDF endpoint renders that same model — so a
 * PDF is always "the model endpoint plus a template". Anything the two could
 * disagree about would have to come from here, and nothing does.
 *
 * `PaperSize` / `Orientation` live here rather than in the web app: they are
 * part of the wire contract, so web imports them from `@releve/shared` instead
 * of keeping its own copies (the client-side generator that owned the old ones
 * was deleted at the cutover — `docs/PDF_GENERATION.md`).
 *
 * Deliberately dropped from the old dialog surface: `format`, `schedule`, the
 * generic `filters` bag, `taxType`, and the unwired currency select. They were
 * never read; a schema that lists them would imply they were.
 */
/* -------------------------------------------------------------------------- */
/* Presentation vocabulary                                                    */
/* -------------------------------------------------------------------------- */
/**
 * Document language. Aliases `documentLanguageSchema` — the single source of
 * truth is `DocumentLanguage` in `../format/locale.ts`. Locale (number/date
 * formatting) is derived from it, never from the currency.
 */
export const pdfLanguageSchema = documentLanguageSchema;
export const PDF_PAPER_SIZES = ["a4", "letter", "legal", "a3"];
export const pdfPaperSizeSchema = z.enum(PDF_PAPER_SIZES);
export const PDF_ORIENTATIONS = ["portrait", "landscape"];
export const pdfOrientationSchema = z.enum(PDF_ORIENTATIONS);
/**
 * `YYYY-MM-DD` calendar date. `z.string().date()` rejects impossible dates
 * (2026-02-30, 2026-02-29) as well as malformed ones, so a bad boundary value
 * fails here with a clean 400 instead of at the RPC a layer deeper.
 */
const isoDate = z.string().date("must be a YYYY-MM-DD calendar date");
/** Fields every document request carries. */
const presentationFields = {
    lang: pdfLanguageSchema.default("en"),
    paperSize: pdfPaperSizeSchema.default("a4"),
};
/**
 * The presentation fields alone — the body of a document request whose scope
 * is in its URL rather than its body (the volunteer report,
 * `volunteerReportRequestSchema`, on the volunteer-signup router).
 */
export const pdfPresentationSchema = z.object(presentationFields);
/* -------------------------------------------------------------------------- */
/* Invoice                                                                    */
/* -------------------------------------------------------------------------- */
/**
 * One endpoint serves both directions: `ar` is a customer invoice, `ap` a
 * vendor bill. The assembler produces one `InvoiceDocument` shape for both
 * (banking details are printed for AR only).
 */
export const pdfInvoiceRequestSchema = z.object({
    invoiceType: invoiceTypeSchema,
    invoiceId: z.string().uuid(),
    ...presentationFields,
});
/* -------------------------------------------------------------------------- */
/* Reports                                                                    */
/* -------------------------------------------------------------------------- */
export const PDF_REPORT_TYPES = [
    "profit-loss",
    "balance-sheet",
    "vat-return",
    "sales-tax-report",
    "trial-balance",
    "cash-flow",
    "debtor-list",
    "creditor-list",
];
export const pdfReportTypeSchema = z.enum(PDF_REPORT_TYPES);
/**
 * Income statement. `viewBy` from the screen's filter state is NOT on the
 * wire: it is derivable — `unallocated` is `unallocatedOnly`, and the
 * per-project comparison block switches on `projectIds.length >= 2`
 * (`MULTI_PROJECT_MINIMUM`).
 *
 * `projectIds` is capped at 20 because multi-project assembly still runs one
 * `get_pnl_account_totals` per project (inherited from the web hook); the
 * batched RPC is a filed follow-up (plan §11).
 */
/**
 * The fund filter, shared by the P&L, the trial balance and the cash flow —
 * one rule for all three (a leg's own `fund_id`; unallocated = no fund on the
 * leg). `fundIds` is capped like `projectIds`: an unbounded id array is work
 * the caller chooses for the server, and a fund filter of any real size is
 * already past what a reader can take in on one statement.
 */
const fundFilterFields = {
    fundIds: z.array(z.string().uuid()).max(20).default([]),
    unallocatedOnly: z.boolean().default(false),
};
export const pdfProfitLossRequestSchema = z.object({
    reportType: z.literal("profit-loss"),
    startDate: isoDate,
    endDate: isoDate,
    projectIds: z.array(z.string().uuid()).max(20).default([]),
    ...fundFilterFields,
    currencyDisplay: incomeStatementCurrencyDisplaySchema.default("translated"),
    orientation: pdfOrientationSchema.default("portrait"),
    ...presentationFields,
});
/**
 * Balance sheet — a point-in-time report, so it takes a single closing date
 * (the screen passes `dateRange.to`, preserving `BalanceSheetPreview`'s
 * precedence).
 */
export const pdfBalanceSheetRequestSchema = z.object({
    reportType: z.literal("balance-sheet"),
    asOfDate: isoDate,
    ...presentationFields,
});
/**
 * VAT return and sales-tax report: two presentations of the SAME
 * `get_tax_report` read, so they share a request shape. `periodLabel` is the
 * human caption ("Q1 2026"); the assembler falls back to the ISO range.
 *
 * Web's `tax-report` / `vat-return` aliasing is normalized to `vat-return` on
 * the wire (plan §2).
 */
const taxReportFields = {
    periodStart: isoDate,
    periodEnd: isoDate,
    periodLabel: z.string().min(1).max(120).optional(),
    ...presentationFields,
};
export const pdfVatReturnRequestSchema = z.object({
    reportType: z.literal("vat-return"),
    ...taxReportFields,
});
export const pdfSalesTaxReportRequestSchema = z.object({
    reportType: z.literal("sales-tax-report"),
    ...taxReportFields,
});
/**
 * Trial balance — opening / movement / closing over a period (the fiscal year
 * by default on screen). Landscape by default: six amount columns.
 */
export const pdfTrialBalanceRequestSchema = z.object({
    reportType: z.literal("trial-balance"),
    startDate: isoDate,
    endDate: isoDate,
    ...fundFilterFields,
    orientation: pdfOrientationSchema.default("landscape"),
    ...presentationFields,
});
/** Cash-flow statement (direct method) over a period, with its prior-year comparative. */
export const pdfCashFlowRequestSchema = z.object({
    reportType: z.literal("cash-flow"),
    startDate: isoDate,
    endDate: isoDate,
    ...fundFilterFields,
    ...presentationFields,
});
/**
 * Debtor and creditor lists — open items at any as-of date (the screen passes
 * the end of its date range, like the balance sheet).
 */
export const pdfDebtorListRequestSchema = z.object({
    reportType: z.literal("debtor-list"),
    asOfDate: isoDate,
    ...presentationFields,
});
export const pdfCreditorListRequestSchema = z.object({
    reportType: z.literal("creditor-list"),
    asOfDate: isoDate,
    ...presentationFields,
});
/**
 * The report request. Discriminated on `reportType` so each report declares
 * exactly the params it reads — an `asOfDate` on a P&L request is a 400, not a
 * silently ignored field.
 *
 * The date-ordering check rides on the OUTER union: a `.superRefine()` applied
 * to a member would turn it into a `ZodEffects`, which
 * `z.discriminatedUnion` does not accept.
 */
export const pdfReportRequestSchema = z
    .discriminatedUnion("reportType", [
    pdfProfitLossRequestSchema,
    pdfBalanceSheetRequestSchema,
    pdfVatReturnRequestSchema,
    pdfSalesTaxReportRequestSchema,
    pdfTrialBalanceRequestSchema,
    pdfCashFlowRequestSchema,
    pdfDebtorListRequestSchema,
    pdfCreditorListRequestSchema,
])
    .superRefine((request, ctx) => {
    if ((request.reportType === "profit-loss" ||
        request.reportType === "trial-balance" ||
        request.reportType === "cash-flow") &&
        request.startDate > request.endDate) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["endDate"],
            message: "endDate must not be before startDate",
        });
    }
    if ((request.reportType === "vat-return" || request.reportType === "sales-tax-report") &&
        request.periodStart > request.periodEnd) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["periodEnd"],
            message: "periodEnd must not be before periodStart",
        });
    }
});
//# sourceMappingURL=pdf.js.map